// tslint:disable:max-line-length

import classNames from "classnames";
import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { push } from "react-router-redux";
import Sticky from "react-stickynode";
import _ from "underscore";
import * as zenscroll from "zenscroll";
import * as WebRoutes from "@travellocal/utils/webRoutes";

import { LazyLoadImage, Loader, Logo, ScrollAnchor } from "../../core/atoms";
import { handleError } from "../../shared/errorHandler";
import { apiPromiseHook, createEnterHookPromise } from "../../shared/flux/actions/enterHooks";
import * as CookieHelper from "../../shared/helpers/cookieHelper";
import { GoogleAnalyticsHelper, sendToDataLayer } from "@travellocal/ui";
import { combineStateStatuses, MatchState, MatchStateStatus } from "../../shared/helpers/reduxHelpers";
import * as UrlHelper from "@travellocal/utils/urlHelpers";
import { StoreActionFactory } from "../../shared/state/storeActionFactory";
import { ContactUsCard } from "../components/contactUsCard";
import * as EnquiryForm from "../components/forms/enquiry2/";
import { FormProgressIcon, QSParam } from "../components/forms/enquiry2/";
import SubmitEnquiryForm from "../components/forms/enquiry2/submitEnquiryForm";
import { HubspotNewsletterSignupForm } from "../components/hubspotForm/hubspotForm";
import { RoutesInvoker } from "../routesInvoker";
import { ActionPromises } from "../state/actionPromises";
import { EnterHookPromises } from "../state/enterHookPromises";
import { formModels } from "../state/forms";
import { api } from "./../../shared/api/system/apiClient";
import { EntityConverter } from "./../../shared/helpers/entityConverter";
import { officeHours } from "./customer/customerEnquiry/utils";

import "./enquire2.scss";

const RootModelPath = "forms.enquiry";
const LocationModelPath = `${RootModelPath}.location`;

interface OwnProps {
  location: {
    search: string;
  };
}

interface StateProps {
  rrfState: Forms.WebFormRoot;
  countriesState: IState<ICountrySummary[]>;
  regionsState: IState<IRegionSummary[]>;
  currencyState: IState<IBaseCurrency>;
  userState: IState<IUser>;
  enquiryThemesState: IPagerState<ITheme>;
  ageRangesState: IState<IAgeRange[]>;
  currenciesState: IState<IBaseCurrency[]>;
  canEnquire: boolean;
}

interface DispatchProps {
  dispatch: any;
  login: (request: ILoginRequest) => DispatchedPromiseResult<ILoginResponse>;
  loginWithToken: (token: string) => DispatchedPromiseResult<ILoginResponse>;
  navigate: (route: string) => any;
}

type Props = OwnProps & StateProps & DispatchProps;

const stateToProps = (state: IWebStoreState): StateProps => {
  const country = state.forms.enquiry ? state.forms.enquiry.location : null;

  return {
    rrfState: state.forms,
    countriesState: state.countries,
    regionsState: state.regions,
    currencyState: state.currency,
    userState: state.user,
    enquiryThemesState: state.enquiryThemes,
    ageRangesState: state.ageRanges,
    currenciesState: state.currencies,
    canEnquire: country != null && country.defaultLto != null && country.locationStatus === LocationStatus.Live,
  };
};

const dispatchToProps = (dispatch): DispatchProps => ({
  dispatch,
  login: req => dispatch(ActionPromises.login(req)),
  loginWithToken: token => dispatch(ActionPromises.loginWithToken(token)),
  navigate: route => dispatch(push(route)),
});

interface State {
  isSubmitting?: boolean;
  submitted?: boolean;
  errorMessages?: string[];
  initialising?: boolean;
  showEmailReset?: boolean;
  tripType?: string;
}

/**
 * New year, new form
 */
class EnquirePage extends React.Component<Props, State> {
  private submitButtonRef: HTMLButtonElement;
  private submitErrorWrapRef: HTMLUListElement;

  public constructor(props: Props) {
    super(props);
    this.state = {
      isSubmitting: false,
      errorMessages: [],
      showEmailReset: false,
    };
  }

  private disableFormSubmission = () => {
    if (this.submitButtonRef) {
      this.submitButtonRef.disabled = true;
    }
  }

  private enableFormSubmission = () => {
    if (this.submitButtonRef) {
      this.submitButtonRef.disabled = false;
    }
  }

  /**
   * Should be called on every failure path for form submission
   */
  private onFormSubmitFailure = () => {
    const shakeElement = this.submitErrorWrapRef || this.submitButtonRef;
    if (!shakeElement) {
      return;
    }

    // Animation takes < 1s
    shakeElement.className += " shake-it ";
    setTimeout(() => {
      if (!shakeElement) {
        return;
      }

      shakeElement.className = shakeElement.className.replace("shake-it", "");

      this.enableFormSubmission();
    }, 1000);
  }

  private handleValidFormSubmit = (formData: Forms.EnquiryForm) => this.setState({ isSubmitting: true, errorMessages: [] }, async () => {

    const onLoginFailure = (state: State) => {
      state.errorMessages = ["Please check you've typed your sign in details correctly, or create a new account"];
      this.setState(state, () => {
        // See account login form for corresponding error message
        this.props.dispatch(actions.setErrors(`${RootModelPath}.account.password`, { signInFailed: true }));
        this.onFormSubmitFailure();
      });
    };

    const onValidationFailure = (state: State, err: IFetchApiFailure) => {
      state.errorMessages = err.validations.map(x => x.message);
      const emailErrorIndex = _.indexOf(state.errorMessages, "Email address is an already existing user email.");
      if (emailErrorIndex >= 0) {
        state.errorMessages[emailErrorIndex] = "You already have an account with us, have you typed your password correctly?";
        state.showEmailReset = true;
      }
      this.setState(state, this.onFormSubmitFailure);
    };

    const onMiscFailure = (state: State, err: IFetchApiFailure) => {
      if (navigator.onLine) {
        state.errorMessages = [
          "Something went wrong - please try again.",
          err.message,
        ];
      }
      else {
        state.errorMessages = [
          "You're offline - please connect to the Internet and try again.",
        ];
      }

      this.setState(state, this.onFormSubmitFailure);
    };

    let isSignedIn = this.props.userState.data != null;
    if (!isSignedIn) {
      // Try to login with account details even if !formData.account.hasAccount
      // Sometimes a user will forget they have an account, but will use the same password anyway
      try {
        const request = { username: formData.account.email.trim(), password: formData.account.password };
        await this.props.login(request);
        isSignedIn = true;
      }
      catch (e) {
        const endState: State = { isSubmitting: false };

        // TODO invalid login, no network
        const err = e as IFetchApiFailure;
        if (!err.response) {
          onMiscFailure(endState, err);
          return;
        }
        else if (err.response.status === 400) {
          if (formData.account.hasAccount) {
            onLoginFailure(endState);
            return;
          }
          else {
            // fall through to account creation
          }
        }
        else if (err.response.status === 422) {
          onValidationFailure(endState, err);
          return;
        }
        else {
          onMiscFailure(endState, err);
          return;
        }
      }
    }

    // formData is frozen, create a deep copy so we can modify it
    formData = JSON.parse(JSON.stringify(formData));
    formData.account.hasAccount = isSignedIn;

    const hubspotCookie = CookieHelper.getCookie("hubspotutk");

    if (hubspotCookie) {
      formData.hubspotCookie = hubspotCookie;
    }

    try {
      const enquiryInput = EntityConverter.enquiryForm3ToInitialEnquiryInput(formData);
      const response = await api.initialEnquiry(enquiryInput);

      this.setState({ isSubmitting: false, submitted: true, errorMessages: [] }, async () => {
        // Enquiry was successful - we'd be in the catch block otherwise

        // Log event to Google
        sendToDataLayer({
          event: "Enquiry",
          formVersion: 3,
          country: formData.location.slug,
          countryId: formData.location.id,
          enquiryId: response.json.id,
          enquiryReference: response.json.reference,
        });

        // Also log events to GA api - TODO remove when we've migrated all analytics to the data layer service
        GoogleAnalyticsHelper.logEvent("Enquiry", `enquiry_submitted`, `Enquiry submitted to ${formData.location.name}`, "Enquiry", response.json.reference);
        GoogleAnalyticsHelper.logPageView(`${location.href}/enquiry-submitted`); // Page doesn't exist - this triggers the goal condition in GA

        const accountCreated = response.json.token != null;
        if (accountCreated) {
          // Account was created; log user in client side
          await this.props.loginWithToken(response.json.token);
        }

        this.props.navigate(RoutesInvoker.customerEnquiriesIndex(response.json.id, accountCreated));

        // Clear out form state, otherwise it'll be here if someone clicks back from the next page
        this.resetForm();
      });
    }
    catch (e) {
      const endState: State = { isSubmitting: false };

      const err = e as IFetchApiFailure;
      if (!err.response) {
        onMiscFailure(endState, err);
      }
      else if (err.response.status === 400 && !formData.account.hasAccount) {
        // Login api always returns 400 even if email is not registered with system
        // Enquiry api will only return 400 if email is registered with the system
        onLoginFailure(endState);
      }
      else if (err.response.status === 422) {
        onValidationFailure(endState, err);
      }
      else {
        onMiscFailure(endState, err);
      }
      return;
    }
  })

  private handleInvalidFormSubmit = () => {
    this.setState({ errorMessages: ["Please check you've filled in all required fields, then try again."] }, this.onFormSubmitFailure);
  }

  private resetForm = () => {
    this.props.dispatch(actions.batch(RootModelPath, [
      actions.reset(RootModelPath),
      actions.load(RootModelPath, formModels.enquiry), // Reset doesn't actually clear out form state fully
    ]));
  }

  private clearCountrySelection = () => {
    zenscroll.toY(0, undefined, () => {
      // can't use reset or setInitial because initial change action from qs parser uses silent param
      this.props.dispatch(actions.change(LocationModelPath, null));

      const enquiryParams = {
        ...this.queryStringWithoutTripType(this.props.location.search),
        campaign: "form-clear",
      };
      this.props.navigate(WebRoutes.enquiryForm(enquiryParams));
    });
  }

  private onCountrySelect = async (location: ILocation) => {
    if (!location) {
      return;
    }

    await this.initialiseFormState(location);

    requestAnimationFrame(() => {
      const enquiryParams = {
        ...this.queryStringWithoutTripType(this.props.location.search),
        locationSlug: location.slug,
        campaign: "form-select",
      };
      this.props.navigate(WebRoutes.enquiryForm(enquiryParams));
    });
  }

  private centreCountrySelector = () => {
    const countrySelector = document.getElementsByClassName("country-selector")[0];
    if (countrySelector) {
      zenscroll.center(countrySelector as HTMLElement);
    }
    else {
      zenscroll.toY(0);
    }
  }

  private initialiseDisplayState = () => {
    const queryString = this.props.location.search.substring(1);
    if (!queryString) {
      return;
    }

    const qs = UrlHelper.getQueryStringValues(queryString);

    // This is needed in the server-side initial render, where initialiseFormState() is not reached.
    if (qs.tripType) {
      this.setState({ tripType: qs.tripType });
    }

  }

  // assume this is only called after the frontend app is on its feet, so use default getQueryStringValues()
  private queryStringWithoutTripType = (search: string): { [key: string]: string | number } => {
    const qs = UrlHelper.getQueryStringValues(search.replace(/^\??/, "")); // strip optional leading ?
    const newQs = _.omit(qs, ["tripType"]);
    return newQs;
  }

  private initialiseFormState = async (location: ILocation) => {
    const qs = UrlHelper.getQueryStringValues();

    // Remove known keys from the query string - navigation between countries will cause them to be reapplied potentially overwriting form state
    const newQs = _.omit(qs, Object.keys(QSParam).map(k => QSParam[k]));
    history.replaceState({}, "", UrlHelper.getCurrentUrlWithQs(newQs));

    // Support pre-filling form from query string
    const qsState = await EnquiryForm.buildFormStateFromQueryString(qs);

    // Add account details to the new state
    const accountState: Forms.EnquiryFormAccount = { ...formModels.enquiry.account }; // Fields that relate to RRF validity need to exist at init
    const user = this.props.userState.data;
    if (user != null) {
      // Add other fields if we need them
      accountState.hasAccount = true;
      if (location && location.enquiryOptions && location.enquiryOptions.phoneNumberRequired) {
        accountState.phoneNumber = user.phoneNumber;
      }
    }

    const stateChanges: Forms.EnquiryForm = {
      ...this.props.rrfState.enquiry,
      ...qsState,
      account: accountState,
      location,
    };

    const rrfActions: any[] = [
      actions.load(RootModelPath, stateChanges),
    ];

    // countrySelect gets unmounted, so sometimes is not correctly validated
    if (stateChanges.location) {
      rrfActions.push(actions.setValidity(LocationModelPath, true));

      if (!stateChanges.location.enquiryOptions || !stateChanges.location.enquiryOptions.phoneNumberRequired) {
        // #421 When switching from when country requiring phone number to one without,
        // phone number field may not be remounted so validity in RRF state may be incorrect
        rrfActions.push(actions.resetErrors("forms.enquiry.account.phoneNumber"));
      }
    }

    await this.props.dispatch(actions.batch(RootModelPath, rrfActions));
  }

  public componentWillMount() {
    this.initialiseDisplayState();
    this.setState({ initialising: true, submitted: false }, async () => {
      this.enableFormSubmission();

      try {
        // Enter hook should have placed the location summary into the formstate
        await this.initialiseFormState(this.props.rrfState.enquiry.location);

        if (this.props.userState.data) {
          // TODO #393 user state can become out of sync on the client because it is not regularly refreshed
          // Refreshing here alleviates the issue for the enquiry form - phone number question will be prepopulated for repeat enquirers
          await this.props.dispatch(ActionPromises.refreshProfile());
        }
      }
      catch (e) {
        handleError(e, this.props.dispatch);
      }
      this.setState({ initialising: false });
    });
  }

  public render() {
    const { regionsState, countriesState, rrfState, userState, dispatch, canEnquire } = this.props;
    const { errorMessages, isSubmitting, showEmailReset } = this.state;
    const formState = rrfState.enquiry;
    const hasCountry = formState.location != null;

    const commonFormProps = { formState, dispatch, user: userState.data };

    let headerImageUrl = "/images/missing-image.jpg";
    let headerImageColour: string;
    let headerImageAltText: string;
    if (hasCountry && formState.location.heroImage) {
      headerImageUrl = formState.location.heroImage.largeUrl;
      headerImageColour = `#${formState.location.heroImage.colour}`;
      headerImageAltText = formState.location.heroImage.meta ?? formState.location.name;
    }

    const phoneNumberRequired = hasCountry && formState.location.enquiryOptions && formState.location.enquiryOptions.phoneNumberRequired;

    const tripTypeContent = { title: null, description: null, header: null, experts: null };
    if (hasCountry && canEnquire) {
      if (this.state.tripType) {
        tripTypeContent.title = tripTypeContent.description = tripTypeContent.header = this.state.tripType;
      }
      else {
        tripTypeContent.title = `trip to ${formState.location.name}`;
        tripTypeContent.description = `holiday to ${formState.location.name}`;
        tripTypeContent.header = ["trip with our local experts in ", (<span className="country-name">{formState.location.name}</span>)];
        tripTypeContent.experts = `in ${formState.location.name} `;
      }
    }

    return (
      <div className={classNames("enquire-page", { "has-country": hasCountry, "no-country": !hasCountry, "can-enquire": canEnquire, "loading": this.state.initialising })}>

        <Helmet
          title={hasCountry && canEnquire ? `Plan your ${tripTypeContent.title}` : "Plan your dream holiday"}
          meta={[
            {
              name: "description",
              content: hasCountry && canEnquire
                ? `Plan your dream ${tripTypeContent.description} with TravelLocal.`
                : "Plan your dream holiday with TravelLocal - connect with our experts from over 45 destinations worldwide.",
            },
          ]}
          link={[
            { rel: "canonical", href: `${globalConfig.wwwBaseUrl}${WebRoutes.enquiryForm({ locationSlug: formState.location ? formState.location.slug : null, campaign: undefined })}` },
          ]}
        />

        <div className="image-header">
          <div className="image-container">
            <LazyLoadImage src={headerImageUrl} alt={headerImageAltText} />
          </div>

          <div id="country-select" style={{ backgroundColor: headerImageColour }}>
            {(hasCountry && canEnquire) && (
              <header>
                <h1>Start planning your {tripTypeContent.header}</h1>
                <p>{`Our local experts ${tripTypeContent.experts} will be in touch shortly after you send your enquiry. Don't worry if your plans might change - just answer every question the best you can!`}</p>
                <p>
                  <a className="switch-country" onClick={this.clearCountrySelection}>
                    Travelling elsewhere? Choose another country
                  </a>
                </p>
              </header>
            )}
            {(hasCountry && !canEnquire) && (
              <header>
                <h1>Coming soon</h1>
                <p>
                  We&apos;re still setting up our partners in {formState.location.name}. {' '}
                  <a className="switch-country" onClick={this.clearCountrySelection}>
                    Click here to choose another country.
                  </a>
                </p>
                <p>Sign up to our newsletter to get notified of new destinations!</p>
                <HubspotNewsletterSignupForm />
              </header>
            )}
            {!hasCountry &&
              MatchStateStatus(combineStateStatuses(regionsState.status, countriesState.status), {
                pending: () => (
                  <header>
                    <h1>Travelling the world...</h1>
                    <Loader />
                  </header>
                ),
                ok: () => {
                  const enabledRegions = regionsState.data.filter(x => x.locationStatus === LocationStatus.Live);
                  const enabledCountries = countriesState.data.filter(x => x.locationStatus === LocationStatus.Live);

                  return (
                    <header>
                      <h1>Where would you like to go?</h1>
                      <p>Choose a country to start planning your trip with one of our local experts.</p>
                      <EnquiryForm.CountrySelector {...commonFormProps}
                        model={LocationModelPath}
                        regions={enabledRegions}
                        countries={enabledCountries}
                        onRegionSelect={this.centreCountrySelector}
                        onCountrySelect={this.onCountrySelect} />
                    </header>
                  );
                },
                error: () => (
                  <header>
                    <h1>Sorry, something went wrong...</h1>
                    <p>Please check your internet connection, then <a onClick={() => window.location.reload()}>click here to reload the page</a>.</p>
                  </header>
                ),
              })}
          </div>

          <Logo type={hasCountry ? "light" : "accent"} link={WebRoutes.index()} />
          <div className="enquire-page-phone-link">
            <a className="heading--3" href="tel:+441173257898"><img src="/images/flags/currency/GBP.png" height={20} alt="United Kingdom flag" />+44 (0)117 325 7898</a>
            <a className="heading--3" href="tel:+18884549902"><img src="/images/flags/currency/USD.png" height={20} alt="American flag" />+1 888 454 9902</a>
          </div>
        </div>

        <Sticky className="no-country-overlay" activeClass="stuck" bottomBoundary="body" enabled={!canEnquire && !this.state.submitted}>
          <div className="wrap">
            <div className="cta" onClick={this.centreCountrySelector}>
              <i className="icon icon-arrow-up3" />
              <p><a>Choose a country to start planning your trip with one of our local experts.</a></p>
            </div>
            <h4>Need a hand?</h4>
            <p>You can call us on <a href="tel:+441173257898">+44 (0)117 325 7898</a>. We're available {officeHours}.</p>
          </div>
        </Sticky>

        <Form method="post" model={RootModelPath} onBeforeSubmit={this.disableFormSubmission} onSubmit={this.handleValidFormSubmit} onSubmitFailed={this.handleInvalidFormSubmit}>
          <div className="columns">
            <div className="form-body">

              {MatchState(this.props.ageRangesState, {
                ok: () => (
                  <EnquiryForm.AgeRanges
                    {...commonFormProps}
                    id="age-range"
                    className="required-field"
                    model={`${RootModelPath}.travellers`}
                    ageRanges={this.props.ageRangesState.data}
                  />
                ),
              })}

              <EnquiryForm.TravelDate {...commonFormProps} id="travel-date" className="required-field" model={`${RootModelPath}.travelDate`} />

              <EnquiryForm.PlanningStageSelect {...commonFormProps} id="planning" className="required-field" model={`${RootModelPath}.planningStage`} />

              {MatchState(this.props.enquiryThemesState, {
                ok: () => (
                  <EnquiryForm.ThemesSelect
                    {...commonFormProps}
                    id="themes"
                    className="required-field"
                    model={`${RootModelPath}.themes`}
                    enquiryThemes={this.props.enquiryThemesState.data}
                  />
                ),
              })}

              <EnquiryForm.IndependenceLevelSelect {...commonFormProps} id="guiding" className="required-field" model={`${RootModelPath}.independenceLevel`} />

              <EnquiryForm.TripTypeSelect {...commonFormProps} id="trip-type" className="required-field" model={`${RootModelPath}.tripType`} />

              {MatchState(this.props.currenciesState, {
                ok: () => (
                  <EnquiryForm.CurrencySelect
                    {...commonFormProps}
                    id="currency"
                    className="required-field"
                    model={`${RootModelPath}.currencyId`}
                    currencies={this.props.currenciesState.data}
                  />
                ),
              })}

              {MatchState(this.props.currencyState, {
                ok: () => (
                  <EnquiryForm.BudgetPerPerson
                    {...commonFormProps}
                    id="budget"
                    className="required-field"
                    model={`${RootModelPath}.budget`}
                    currency={this.props.currencyState.data}
                  />
                ),
              })}

              {phoneNumberRequired &&
                <EnquiryForm.PhoneNumber {...commonFormProps} id="phone-number" className="required-field" model={`${RootModelPath}.account.phoneNumber`} />
              }

              <EnquiryForm.NotesField {...commonFormProps} id="notes" className="required-field" model={`${RootModelPath}.notes`} />

              <EnquiryForm.RecommendedBy {...commonFormProps} id="recommended-by" model={`${RootModelPath}.recommendedBy`} />
            </div>

            <Sticky className="sidebar" bottomBoundary=".form-body">
              <ContactUsCard />

              <div className="progress-list">
                <ul>
                  <li>
                    <div className="enquire-progress-icon valid">
                      <div className="icon-wrap">
                        <i className="icon icon-checkmark" />
                      </div>
                      <ScrollAnchor target="country-select">Where would you like to go?</ScrollAnchor>
                    </div>
                  </li>
                  <li>
                    <FormProgressIcon model={`${RootModelPath}.travellers`}><ScrollAnchor target="age-range">Who's travelling?</ScrollAnchor></FormProgressIcon>
                  </li>
                  <li>
                    <FormProgressIcon model={`${RootModelPath}.travelDate`}><ScrollAnchor target="travel-date">When would you like to go?</ScrollAnchor></FormProgressIcon>
                  </li>
                  <li>
                    <FormProgressIcon model={`${RootModelPath}.planningStage`}><ScrollAnchor target="planning">How much planning have you done?</ScrollAnchor></FormProgressIcon>
                  </li>
                  <li>
                    <FormProgressIcon model={`${RootModelPath}.themes`}><ScrollAnchor target="themes">What kind of holiday?</ScrollAnchor></FormProgressIcon>
                  </li>
                  <li>
                    <FormProgressIcon model={`${RootModelPath}.independenceLevel`}><ScrollAnchor target="guiding">How much guiding?</ScrollAnchor></FormProgressIcon>
                  </li>
                  <li>
                    <FormProgressIcon model={`${RootModelPath}.tripType`}><ScrollAnchor target="trip-type">What kind of accommodation?</ScrollAnchor></FormProgressIcon>
                  </li>
                  <li>
                    <FormProgressIcon model={`${RootModelPath}.currencyId`}><ScrollAnchor target="currency">What's your currency?</ScrollAnchor></FormProgressIcon>
                  </li>
                  <li>
                    <FormProgressIcon model={`${RootModelPath}.budget`}><ScrollAnchor target="budget">What's your budget per person?</ScrollAnchor></FormProgressIcon>
                  </li>
                  {phoneNumberRequired &&
                    <li>
                      <FormProgressIcon model={`${RootModelPath}.account.phoneNumber`}><ScrollAnchor target="phone-number">What's your phone number?</ScrollAnchor></FormProgressIcon>
                    </li>
                  }
                  <li>
                    <FormProgressIcon model={`${RootModelPath}.notes`}><ScrollAnchor target="notes">Any comments?</ScrollAnchor></FormProgressIcon>
                  </li>
                </ul>
              </div>
            </Sticky>
          </div>

          <section className="account-form-wrap">
            <header>
              <h2>One last thing...</h2>
              <p>To start planning your holiday, you need a TravelLocal account. With your email address and a password, you will be able to plan your trip from any phone or computer.</p>
            </header>
            <div className="account-question">
              <EnquiryForm.Account {...commonFormProps} model={`${RootModelPath}.account`} userState={userState} />
              <SubmitEnquiryForm
                setSubmitButtonRef={x => this.submitButtonRef = x}
                setSubmitErrorWrapRef={x => this.submitErrorWrapRef = x}
                errorMessages={errorMessages}
                headerImageColour={headerImageColour}
                isSubmitting={isSubmitting}
                showEmailReset={showEmailReset}
              />
            </div>
          </section>
        </Form>
      </div>
    );
  }
}

export default connect(stateToProps, dispatchToProps)(EnquirePage);

export const enquirePageOnEnter = apiPromiseHook<any>(
  EnterHookPromises.enquiryThemes(30),
  EnterHookPromises.allAgeRanges(),
  createEnterHookPromise<IWebStoreState>(router => {
    const locationSlug = router.params.locationSlug;
    if (locationSlug == null) {
      return actions.reset(LocationModelPath);
    }

    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
      const { forms } = getState();
      if (forms.enquiry.location && forms.enquiry.location.slug === locationSlug) {
        return;
      }

      try {
        const r = await api.getLocationBySlug(locationSlug);
        if (r.json != null) {
          return dispatch(actions.change(LocationModelPath, r.json));
        }
      }
      finally {
        // On error, e.g. if api is down or location with this slug doesn't exist page will just show country selector with incorrect URL
        // Probably ok from a user perspective
      }
    });
  }),
);
